import {
    ConfigurationContext,
    ElectionDefinition,
    queries,
    useElectionToggle,
    useFeature,
} from '@news-mono/web-common'
import { useQuery } from '@tanstack/react-query'
import React, { useContext, useMemo } from 'react'
import { useLocation } from 'react-router'
import { ElectionTitleButton } from '../components'
import { SeatData, getSeatsData } from '../data'
import { SeatCard } from '../SeatCard/SeatCard'
import { breakpoint } from '../../../__styling'
import {
    TheSeatEmbedWidgetContainer,
    TheSeatEmbedWidgetLogo,
    TheSeatEmbedWidgetLogoLink,
    TheSeatEmbedWidgetTitleContainer,
} from './TheSeatEmbedWidget.styled'

export interface TheSeatEmbedWidgetProps {
    electionDefinition: ElectionDefinition
    mainRef?: React.RefObject<HTMLDivElement>
    fireClickEvent?: () => void
    electorateName?: string | null
    providedData?: SeatData
    imageBaseUrl?: string
}

export const TheSeatEmbedWidget = ({
    electionDefinition,
    mainRef,
    electorateName,
    providedData,
    fireClickEvent,
    imageBaseUrl,
}: TheSeatEmbedWidgetProps) => {
    const electionEmbedsEnabled = useFeature('custom-election-embeds')

    const waElectoratesPageEnabled = useFeature(
        'wa-election-electorate-results-route',
    )
    const federalElectoratesPageEnabled = useFeature(
        'federal-election-electorate-results-route',
    )

    const areElectorateRoutesEnabled =
        waElectoratesPageEnabled || federalElectoratesPageEnabled

    const config = useContext(ConfigurationContext)
    const result = useQuery(
        queries['election-api'].definition({
            initialDefinition: electionDefinition,
            electionAPI: config.electionApi,
            caller: config.apiCallerHeader,
        }),
    )

    const isLandingPageEnabled = useElectionToggle(
        electionDefinition.electionId,
        'election-landing-page-route',
    )

    const { search } = useLocation()
    const params = new URLSearchParams(search)

    // no name or data is provided, so assume it's not an embed, and we want to provide the actual
    // name from the provided url.
    if (!electorateName && !providedData) {
        electorateName = params.get('electorate')
    }

    // Calculate seatData.
    const seatData = useMemo(() => {
        // Check against the loaded data now!
        if (result.isSuccess && result.data.electionData.data) {
            const seats = getSeatsData(
                result.data.electionData.data,
                imageBaseUrl,
                64,
            )

            return seats.find(
                (seat) =>
                    seat.seatName.toLowerCase() ===
                    electorateName?.toLowerCase(),
            )
        }
    }, [
        imageBaseUrl,
        electorateName,
        result.isSuccess,
        result.data?.electionData.data,
    ])

    const { logo } = electionDefinition
    const logoPicture = (
        <TheSeatEmbedWidgetLogo>
            {logo.desktop && (
                <source
                    media={breakpoint('sm').replace('@media ', '')}
                    srcSet={logo.desktop}
                />
            )}
            <img src={logo.mobile} alt={logo.alt} />
        </TheSeatEmbedWidgetLogo>
    )

    const electionId = result.isSuccess ? result.data.electionId : undefined

    const ViewElectoratesButton = ({
        desktopButton,
        seatName,
        electionId,
    }: {
        desktopButton: boolean
        seatName: string | undefined
        electionId: string
    }) => (
        <ElectionTitleButton
            to={
                seatName
                    ? `/politics/${electionId}/electorates?search=${seatName.toLowerCase()}&display=expanded`
                    : `/politics/${electionId}/electorates`
            }
            includeBorder={true}
            hideOnMobile={desktopButton}
            hideOnDesktop={!desktopButton}
            fireEvent={fireClickEvent}
            text={'View Electorates'}
        />
    )

    return electionEmbedsEnabled ? (
        <TheSeatEmbedWidgetContainer ref={mainRef}>
            <TheSeatEmbedWidgetTitleContainer>
                {isLandingPageEnabled ? (
                    <TheSeatEmbedWidgetLogoLink
                        to={`/politics/${electionDefinition.electionId}`}
                        aria-label={`Go to the ${logo.alt} page`}
                        onClick={fireClickEvent}
                    >
                        {logoPicture}
                    </TheSeatEmbedWidgetLogoLink>
                ) : (
                    logoPicture
                )}
                {areElectorateRoutesEnabled && electionId && (
                    <ViewElectoratesButton
                        desktopButton
                        seatName={seatData?.seatName}
                        electionId={electionId}
                    />
                )}
            </TheSeatEmbedWidgetTitleContainer>
            {seatData ? (
                <>
                    <SeatCard isEmbed seat={seatData} />
                </>
            ) : (
                <SeatCard isLoading isEmbed />
            )}
            {areElectorateRoutesEnabled && electionId && (
                <ViewElectoratesButton
                    desktopButton={false}
                    seatName={seatData?.seatName}
                    electionId={electionId}
                />
            )}
        </TheSeatEmbedWidgetContainer>
    ) : null
}
